import { Message } from '../types/ai';
import { processMessageSent } from './plans-service';
import { configService } from './config-service';
import { webSearchService } from './web-search-service';
import { fileGenerationService } from './file-generation-service';
import { calendarToolsService } from './calendar-tools-service';
import { saiposToolsService } from './saipos-tools-service';

export class AIService {
  public static instance: AIService;

  private constructor() {
    // Validar se pelo menos uma API está configurada
    try {
      configService.validateMinimumConfiguration();
    } catch (err) {
      const error = err as Error;
      console.warn('⚠️ APIs não configuradas, usando modo demonstração:', error.message);
      console.info('💡 Configure as chaves de API no arquivo .env para usar as APIs reais');
    }
  }

  static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  /** Timeout para chamadas ao openai-proxy (evita loading infinito). */
  private static readonly OPENAI_FETCH_TIMEOUT_MS = 120_000;

  private async fetchWithTimeout(url: string, init: RequestInit, timeoutMs = AIService.OPENAI_FETCH_TIMEOUT_MS): Promise<Response> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...init, signal: controller.signal });
      clearTimeout(id);
      return res;
    } catch (e: any) {
      clearTimeout(id);
      if (e?.name === 'AbortError') {
        throw new Error('A resposta da IA demorou muito. Tente novamente.');
      }
      throw e;
    }
  }

  private buildSystemPrompt(contentRestrictions: any): string {
    if (!contentRestrictions?.enabled) {
      return '';
    }

    let systemPrompt = `Você deve seguir estas diretrizes de conteúdo:

`;

    if (contentRestrictions.restrictedTopics?.length > 0) {
      systemPrompt += `Tópicos restritos (não discutir): ${contentRestrictions.restrictedTopics.join(', ')}
`;
    }

    if (contentRestrictions.customInstructions) {
      systemPrompt += `Instruções específicas: ${contentRestrictions.customInstructions}
`;
    }

    if (contentRestrictions.blockSensitiveContent) {
      systemPrompt += `Evite conteúdo sensível, ofensivo ou inadequado.
`;
    }

    return systemPrompt;
  }

  /**
   * Converter File para base64
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remover o prefixo "data:image/...;base64,"
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Redimensiona e comprime uma imagem se necessário
   * Claude tem limite de 5MB, então redimensionamos para garantir
   */
  private async compressImage(file: File, maxSizeMB: number = 5): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Calcular dimensões para manter proporção
          let width = img.width;
          let height = img.height;
          const maxDimension = 2048; // Máximo recomendado pelo Claude
          
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          // Criar canvas para redimensionar
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Não foi possível criar contexto do canvas'));
            return;
          }
          
          // Desenhar imagem redimensionada
          ctx.drawImage(img, 0, 0, width, height);
          
          // Tentar diferentes qualidades até ficar abaixo do limite
          const tryCompress = (quality: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Erro ao comprimir imagem'));
                  return;
                }
                
                const sizeMB = blob.size / (1024 * 1024);
                
                if (sizeMB > maxSizeMB && quality > 0.1) {
                  // Tentar com qualidade menor
                  tryCompress(quality - 0.1);
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg', // Converter para JPEG para melhor compressão
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                }
              },
              'image/jpeg',
              quality
            );
          };
          
          // Começar com qualidade 0.9
          tryCompress(0.9);
        };
        
        img.onerror = () => reject(new Error('Erro ao carregar imagem'));
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  }

  // Armazena o userId atual para usar nas chamadas de tools
  private currentUserId: string | null = null;

  /**
   * Processa chamadas de função do GPT (tool calls)
   */
  private async processToolCalls(toolCalls: any[]): Promise<{ 
    responses: any[]; 
    files: any[];
    executedTools: Array<{
      toolId: string;
      operationId: string;
      operationName: string;
      parameters: any;
      status: 'completed' | 'failed';
      result?: any;
      error?: string;
      httpStatus?: number;
      startedAt: Date;
      completedAt: Date;
    }>;
  }> {
    const toolResponses = [];
    const generatedFiles = [];
    const executedTools = [];

    for (const toolCall of toolCalls) {
      const functionName = toolCall.function.name;
      const functionArgs = JSON.parse(toolCall.function.arguments);
      const startedAt = new Date();

      if (functionName === 'web_search') {
        try {
          const searchResults = await webSearchService.executeWebSearch(functionArgs.query);
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: searchResults
          });
          executedTools.push({
            toolId: 'web_search',
            operationId: 'search',
            operationName: 'Busca na Web',
            parameters: functionArgs,
            status: 'completed' as const,
            result: { query: functionArgs.query, resultsCount: searchResults.split('\n\n').length - 1 },
            startedAt,
            completedAt: new Date()
          });
        } catch (error) {
          console.error('❌ Erro ao executar busca:', error);
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: `Erro ao buscar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
          });
          executedTools.push({
            toolId: 'web_search',
            operationId: 'search',
            operationName: 'Busca na Web',
            parameters: functionArgs,
            status: 'failed' as const,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            startedAt,
            completedAt: new Date()
          });
        }
      } 
      
      // 🗓️ GOOGLE CALENDAR
      else if (functionName === 'calendar_list_events' || functionName === 'calendar_create_event') {
        try {
          if (!this.currentUserId) {
            throw new Error('Usuário não identificado para acessar o calendário');
          }
          
          const calendarResult = await calendarToolsService.executeCalendarTool(
            functionName,
            functionArgs,
            this.currentUserId
          );
          
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: calendarResult.formattedResponse
          });
          
          executedTools.push({
            toolId: 'google_calendar',
            operationId: functionName === 'calendar_list_events' ? 'list_events' : 'create_event',
            operationName: functionName === 'calendar_list_events' ? 'Listar Eventos' : 'Criar Evento',
            parameters: functionArgs,
            status: calendarResult.success ? 'completed' as const : 'failed' as const,
            result: calendarResult.result,
            error: calendarResult.error,
            startedAt,
            completedAt: new Date()
          });
        } catch (error) {
          console.error('❌ Erro ao executar operação do calendário:', error);
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: `❌ Erro ao acessar calendário: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
          });
          executedTools.push({
            toolId: 'google_calendar',
            operationId: functionName === 'calendar_list_events' ? 'list_events' : 'create_event',
            operationName: functionName === 'calendar_list_events' ? 'Listar Eventos' : 'Criar Evento',
            parameters: functionArgs,
            status: 'failed' as const,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            startedAt,
            completedAt: new Date()
          });
        }
      }
      
      // 🍽️ SAIPOS
      else if (functionName.startsWith('saipos_')) {
        try {
          if (!this.currentUserId) {
            throw new Error('Usuário não identificado para acessar o Saipos');
          }
          
          const saiposResult = await saiposToolsService.executeSaiposTool(
            functionName,
            functionArgs,
            this.currentUserId
          );
          
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: saiposResult.formattedResponse
          });
          
          // Mapear functionName para operationId
          const operationIdMap: Record<string, string> = {
            'saipos_get_sales': 'get_sales',
            'saipos_get_sale_items': 'get_sale_items',
            'saipos_get_stock_position': 'get_stock_position',
            'saipos_get_catalog': 'get_catalog'
          };
          
          const operationNameMap: Record<string, string> = {
            'saipos_get_sales': 'Consultar Vendas',
            'saipos_get_sale_items': 'Consultar Itens da Venda',
            'saipos_get_stock_position': 'Estoque',
            'saipos_get_catalog': 'Consultar Cardápio'
          };
          
          executedTools.push({
            toolId: 'saipos',
            operationId: operationIdMap[functionName] || functionName,
            operationName: operationNameMap[functionName] || functionName,
            parameters: functionArgs,
            status: saiposResult.success ? 'completed' as const : 'failed' as const,
            result: saiposResult.result,
            error: saiposResult.error,
            httpStatus: saiposResult.httpStatus,
            startedAt,
            completedAt: new Date()
          });
        } catch (error) {
          console.error('❌ Erro ao executar operação do Saipos:', error);
          const err = error as any;
          const httpStatus = err?.httpStatus ?? (typeof err?.httpStatus === 'number' ? err.httpStatus : undefined);
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: `❌ Erro ao acessar Saipos: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
          });
          executedTools.push({
            toolId: 'saipos',
            operationId: functionName.replace('saipos_', ''),
            operationName: 'Operação Saipos',
            parameters: functionArgs,
            status: 'failed' as const,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            httpStatus,
            startedAt,
            completedAt: new Date()
          });
        }
      }
      
      else if (functionName === 'generate_file') {
        try {
          const file = await fileGenerationService.generateFile(functionArgs);
          generatedFiles.push(file);
          
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: `✅ Arquivo gerado com sucesso: ${file.filename} (${file.size} bytes). O usuário pode fazer download agora.`
          });
          executedTools.push({
            toolId: 'generate_file',
            operationId: 'generate',
            operationName: 'Gerar Arquivo',
            parameters: functionArgs,
            status: 'completed' as const,
            result: { filename: file.filename, size: file.size },
            startedAt,
            completedAt: new Date()
          });
        } catch (error) {
          console.error('❌ Erro ao gerar arquivo:', error);
          toolResponses.push({
            tool_call_id: toolCall.id,
            role: 'tool',
            name: functionName,
            content: `Erro ao gerar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
          });
          executedTools.push({
            toolId: 'generate_file',
            operationId: 'generate',
            operationName: 'Gerar Arquivo',
            parameters: functionArgs,
            status: 'failed' as const,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            startedAt,
            completedAt: new Date()
          });
        }
      }
    }

    return { responses: toolResponses, files: generatedFiles, executedTools };
  }

  /**
   * Chamar API do ChatGPT/OpenAI via Supabase Edge Function
   * Retorna conteúdo e tokens usados
   */
  async callGPT(messages: Message[], modelId: string, contentRestrictions?: any, enabledTools?: string[], forcedTools?: string[]): Promise<{ content: string; tokens?: any; files?: any[]; toolCalls?: any[] }> {
    console.log('🚀 callGPT iniciado com:', {
      messagesCount: messages.length,
      modelId,
      enabledTools,
      forcedTools
    });
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL não configurado');
    }

    try {
      // Chamando OpenAI via Edge Function
      
      // Verificar se o modelo suporta imagens (Vision)
      const supportsVision = modelId.includes('gpt-4o') || 
                            modelId.includes('gpt-4-vision') || 
                            modelId.includes('gpt-4-turbo');
      
      // ✨ ADICIONAR INSTRUÇÃO DE SISTEMA PARA GERAÇÃO PROATIVA DE ARQUIVOS
      const hasSystemMessage = messages.some(msg => msg.role === 'system');
      const systemInstructions = `
🚨🚨🚨 REGRA ABSOLUTA - SEGUIR SEM QUESTIONAR 🚨🚨🚨

SEMPRE que o usuário pedir:
- "planilha", "histórico", "dados", "tabela", "preços", "últimos X dias" → CSV
- "pdf", "documento", "relatório", "análise", "resumo em pdf" → PDF

👉 VOCÊ DEVE CHAMAR generate_file NA MESMA RESPOSTA
👉 NÃO há opção de NÃO gerar
👉 NÃO há "se quiser"
👉 NÃO há "posso gerar"
👉 APENAS GERE

❌❌❌ PROIBIDO (LINKS NÃO FUNCIONAM):
- ❌ "Download do PDF - [título]"
- ❌ "@https://exemplo.com/arquivo.pdf"
- ❌ "[Baixar](http://link/arquivo.pdf)"
- ❌ "Aqui está o PDF resumido com..." seguido de link markdown

✅✅✅ OBRIGATÓRIO:
1. CHAMAR generate_file({type: "pdf", title: "...", content: "...", filename: "...pdf"})
2. Esperar "✅ Arquivo gerado"
3. Responder: "Aqui está o arquivo! Use o botão de download acima. ⬇️"

FLUXO CORRETO PARA PDF:
1. Se o usuário pede PDF → CHAMAR generate_file
2. NÃO criar links de markdown
3. NÃO escrever "Download do PDF"
4. APENAS chamar a função e confirmar

FLUXO CORRETO PARA CSV:
1. Buscar dados (web_search se necessário)
2. CHAMAR generate_file IMEDIATAMENTE
3. Apresentar: "Aqui está sua planilha! ⬇️"

VOCÊ NÃO PODE PULAR generate_file. É MANDATÓRIO.
LINKS DE MARKDOWN NÃO FUNCIONAM. APENAS A FUNÇÃO generate_file FUNCIONA.`.trim();

      // Preparar mensagens para a API do OpenAI (com suporte a imagens se o modelo suportar)
      const apiMessages = await Promise.all(messages.map(async msg => {
        // ✅ PRESERVAR mensagens 'system' (instruções dos assistentes)
        if (msg.role === 'system') {
          return {
            role: 'system',
            content: msg.content
          };
        }

        // Se a mensagem tem anexos de imagem E o modelo suporta vision
        if (supportsVision && msg.attachments && msg.attachments.length > 0) {
          const imageAttachments = msg.attachments.filter(att => att.type.startsWith('image/'));
          
          if (imageAttachments.length > 0) {
            // Formato para GPT-4 Vision
            const content: any[] = [
              {
                type: 'text',
                text: msg.content || 'Veja esta imagem:'
              }
            ];

            // Adicionar cada imagem
            for (const attachment of imageAttachments) {
              if (attachment.file) {
                
                // Verificar se é um File/Blob válido
                if (!(attachment.file instanceof Blob)) {
                  console.warn('⚠️ Arquivo não é um Blob válido, pulando:', attachment.name);
                  continue;
                }
                
                // OpenAI tem limite de 20MB, comprimir se necessário
                const fileSizeMB = attachment.file.size / (1024 * 1024);
                let fileToSend = attachment.file;
                
                if (fileSizeMB > 18) { // Comprimir se maior que 18MB para margem de segurança
                  fileToSend = await this.compressImage(attachment.file, 18);
                }
                
                const base64 = await this.fileToBase64(fileToSend);
                content.push({
                  type: 'image_url',
                  image_url: {
                    url: `data:${fileToSend.type};base64,${base64}`
                  }
                });
              } else if (attachment.url) {
                content.push({
                  type: 'image_url',
                  image_url: {
                    url: attachment.url
                  }
                });
              }
            }

            return {
              role: msg.role === 'assistant' ? 'assistant' : 'user',
              content
            };
          }
        }

        // Mensagem de texto simples (ou modelo não suporta vision)
        // Se tem imagens mas modelo não suporta, adicionar aviso no texto
        if (!supportsVision && msg.attachments && msg.attachments.length > 0) {
          const imageAttachments = msg.attachments.filter(att => att.type.startsWith('image/'));
          if (imageAttachments.length > 0) {
            const imageNames = imageAttachments.map(att => att.name).join(', ');
            return {
              role: msg.role === 'assistant' ? 'assistant' : 'user',
              content: msg.content + `\n\n[Nota: ${imageAttachments.length} imagem(ns) anexada(s): ${imageNames}. Use GPT-4o ou GPT-4 Vision para analisar imagens.]`
            };
          }
        }

        return {
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        };
      }));

      // ✨ ADICIONAR INSTRUÇÃO DE SISTEMA SOBRE GERAÇÃO DE ARQUIVOS (SEMPRE!)
      // Isso DEVE ser a primeira mensagem para garantir que GPT siga
      apiMessages.unshift({
        role: 'system',
        content: systemInstructions
      });

      // ✨ REFORÇO ADICIONAL: Adicionar como "developer" message se suportado
      // GPT-4o suporta role "developer" que tem prioridade ainda maior
      if (modelId.includes('gpt-4o') || modelId.includes('gpt-4-turbo')) {
        apiMessages.unshift({
          role: 'developer' as any, // TypeScript pode reclamar, mas OpenAI aceita
          content: `SISTEMA CRÍTICO: generate_file é OBRIGATÓRIO quando houver dados tabulares. Você DEVE chamar generate_file na mesma resposta, sem exceções. Proibido: "se quiser", "posso gerar", "quer que eu". Obrigatório: [chama generate_file] "Aqui está! ⬇️"`
        });
      }

      // Adicionar prompt do sistema com restrições se fornecidas
      if (contentRestrictions && contentRestrictions.enabled) {
        const systemPrompt = this.buildSystemPrompt(contentRestrictions);
        if (systemPrompt) {
          apiMessages.unshift({
            role: 'system',
            content: systemPrompt
          });
        }
      }

      // Preparar request para OpenAI
      // ✨ Construir lista de tools baseado no que está habilitado
      const activeTools: any[] = [];
      
      // Só adiciona web_search se estiver explicitamente habilitado
      if (enabledTools?.includes('web_search')) {
        activeTools.push(...webSearchService.getWebSearchTools());
      }
      
      // Adiciona Google Calendar se estiver habilitado
      if (enabledTools?.includes('google_calendar')) {
        activeTools.push(...calendarToolsService.getCalendarTools());
      }
      
      // Adiciona Saipos se estiver habilitado
      if (enabledTools?.includes('saipos')) {
        activeTools.push(...saiposToolsService.getSaiposTools());
      }
      
      // File generation só é incluído se:
      // 1. Estiver explicitamente habilitado OU
      // 2. Não houver nenhuma tool de assistente configurada (uso genérico)
      const hasAssistantTools = enabledTools && enabledTools.length > 0 && 
        enabledTools.some(t => ['google_calendar', 'google_drive', 'saipos'].includes(t));
      
      if (!hasAssistantTools) {
        activeTools.push(fileGenerationService.getFileGenerationTool());
      }

      // 🎯 FERRAMENTAS FORÇADAS: Quando o usuário explicitamente solicita uma ferramenta
      if (forcedTools && forcedTools.length > 0) {
        console.log('🎯 Processando ferramentas forçadas:', forcedTools);
        
        // Garantir que as ferramentas forçadas estejam disponíveis
        if (forcedTools.includes('web_search') && !activeTools.some(t => t.function?.name === 'web_search')) {
          console.log('📌 Adicionando web_search às ferramentas ativas');
          activeTools.push(...webSearchService.getWebSearchTools());
        }
        
        // Garantir que Google Calendar esteja disponível se forçado
        if (forcedTools.includes('google_calendar') && !activeTools.some(t => t.function?.name?.startsWith('calendar_'))) {
          console.log('📌 Adicionando google_calendar às ferramentas ativas');
          activeTools.push(...calendarToolsService.getCalendarTools());
        }
        
        // Garantir que Saipos esteja disponível se forçado
        if (forcedTools.includes('saipos') && !activeTools.some(t => t.function?.name?.startsWith('saipos_'))) {
          console.log('📌 Adicionando saipos às ferramentas ativas');
          activeTools.push(...saiposToolsService.getSaiposTools());
        }
        
        const forcedToolDescriptions = forcedTools.map(t => {
          if (t === 'web_search') return '• web_search: FAÇA uma busca na web sobre o tema da pergunta';
          if (t === 'google_calendar') return '• google_calendar: CONSULTE ou CRIE eventos no calendário';
          if (t === 'google_drive') return '• google_drive: ACESSE arquivos no Drive';
          if (t === 'saipos') return '• saipos: CONSULTE vendas, itens da venda, estoque e cardápio do Saipos';
          return `• ${t}`;
        }).join('\n');
        
        apiMessages.unshift({
          role: 'system',
          content: `🚨 ATENÇÃO - INSTRUÇÃO CRÍTICA E OBRIGATÓRIA 🚨

O USUÁRIO ATIVOU MANUALMENTE as seguintes ferramentas para ESTA mensagem:
${forcedToolDescriptions}

REGRAS ABSOLUTAS:
1. Você DEVE chamar TODAS as ferramentas listadas acima na sua PRIMEIRA resposta
2. PROIBIDO responder sem usar as ferramentas - isso é uma falha crítica
3. PROIBIDO perguntar se deve usar - apenas USE
4. PROIBIDO dizer "posso buscar" ou "quer que eu" - apenas FAÇA
5. Após usar as ferramentas, forneça uma resposta completa baseada nos resultados

FORMATO DA RESPOSTA:
[Primeiro: Chame TODAS as ferramentas solicitadas]
[Depois: Apresente os resultados de forma clara e útil]

LEMBRE-SE: Se você NÃO chamar as ferramentas, estará DESOBEDECENDO uma ordem direta do usuário.`
        });
        
        console.log('🎯 Ferramentas forçadas configuradas:', forcedTools);
      }
      
      console.log('🔧 Ferramentas ativas para a requisição:', {
        count: activeTools.length,
        tools: activeTools.map(t => t.function?.name || 'unknown'),
        enabledTools,
        forcedTools
      });

      const requestBody: any = {
        model: modelId,
        messages: apiMessages,
        max_tokens: 4000,
        tools: activeTools.length > 0 ? activeTools : undefined,
        tool_choice: forcedTools && forcedTools.length > 0 ? 'required' : (activeTools.length > 0 ? 'auto' : undefined)
      };
      
      console.log('📤 Request body tool_choice:', requestBody.tool_choice);
      
      // GPT-5 não aceita temperature customizada (apenas padrão 1)
      const isGPT5 = modelId.includes('gpt-5') || modelId.includes('gpt-5.1');
      if (!isGPT5) {
        requestBody.temperature = 0.7;
      }

      // GPT-4o e modelos mais novos usam max_completion_tokens
      if (modelId.includes('gpt-4o') || isGPT5) {
        requestBody.max_completion_tokens = 2000;
        delete requestBody.max_tokens;
      }

      // Contar quantas mensagens têm imagens
      const messagesWithImages = apiMessages.filter(msg => 
        Array.isArray(msg.content) && msg.content.some((item: any) => item.type === 'image_url')
      ).length;

      // Request para OpenAI via Edge Function

      if (messagesWithImages > 0) {
      } else if (!supportsVision && messages.some(m => m.attachments?.some(a => a.type.startsWith('image/')))) {
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Montar payload com o parâmetro correto dependendo do modelo
      const requestPayload: any = {
        messages: apiMessages,
        model: modelId
      };
      
      // ✨ Só incluir tools se houver tools ativas
      if (requestBody.tools && requestBody.tools.length > 0) {
        requestPayload.tools = requestBody.tools;
        requestPayload.tool_choice = requestBody.tool_choice;
      }
      
      // GPT-5 não aceita temperature customizada (apenas valor padrão 1)
      // Modelos antigos aceitam temperature customizada
      const isGPT5Model = modelId.includes('gpt-5') || modelId.includes('gpt-5.1');
      if (!isGPT5Model) {
        requestPayload.temperature = 0.7;
      }
      
      // Usar max_completion_tokens para modelos novos, max_tokens para antigos
      if (requestBody.max_completion_tokens) {
        requestPayload.max_completion_tokens = requestBody.max_completion_tokens;
      } else {
        requestPayload.max_tokens = requestBody.max_tokens || 2000;
      }

      const response = await this.fetchWithTimeout(
        `${supabaseUrl}/functions/v1/openai-proxy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify(requestPayload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        
        // Tratar erros específicos
        if (response.status === 401) {
          throw new Error('Chave de API inválida. Verifique sua chave do OpenAI.');
        } else if (response.status === 429) {
          throw new Error('Limite de uso da API excedido. Tente novamente mais tarde.');
        } else if (response.status === 403) {
          throw new Error('Acesso negado. Verifique se sua chave de API tem as permissões necessárias.');
        } else {
          throw new Error(`Erro da API OpenAI: ${response.status} - ${errorText}`);
        }
      }

      let data = await response.json();
      
      let message = data.choices?.[0]?.message;
      
      console.log('📥 Resposta inicial do GPT:', {
        hasMessage: !!message,
        hasContent: !!message?.content,
        contentPreview: message?.content?.substring(0, 100),
        hasToolCalls: !!message?.tool_calls,
        toolCallsCount: message?.tool_calls?.length || 0,
        toolCalls: message?.tool_calls?.map((tc: any) => tc.function?.name)
      });
      
      // ✨ PROCESSAR TOOL CALLS (se o GPT pediu para usar ferramentas)
      let generatedFiles: any[] = [];
      let allExecutedTools: any[] = [];
      
      if (message?.tool_calls && message.tool_calls.length > 0) {
        console.log('🔧 GPT solicitou ferramentas, processando...', {
          toolCallsCount: message.tool_calls.length,
          toolCalls: message.tool_calls.map((tc: any) => ({
            id: tc.id,
            name: tc.function?.name,
            args: tc.function?.arguments
          }))
        });
        
        // Executar as funções solicitadas
        const { responses: toolResponses, files, executedTools } = await this.processToolCalls(message.tool_calls);
        generatedFiles = files;
        allExecutedTools.push(...executedTools);
        
        console.log('✅ Ferramentas processadas:', {
          responsesCount: toolResponses.length,
          filesCount: files.length,
          executedToolsCount: executedTools.length,
          executedTools: executedTools.map(t => ({
            toolId: t.toolId,
            operationId: t.operationId,
            status: t.status
          }))
        });
        
        // Adicionar a resposta do assistente com tool_calls
        apiMessages.push(message);
        
        // Adicionar as respostas das ferramentas
        apiMessages.push(...toolResponses);
        
        // Instrução crítica: o agente DEVE basear a resposta nos dados da API retornados pelas tools
        const toolResponseInstruction: { role: 'system'; content: string } = {
          role: 'system',
          content: 'REGRA OBRIGATÓRIA: As mensagens com role "tool" contêm a resposta da API. Sua próxima resposta DEVE ser gerada EXATAMENTE com base nos dados dessas mensagens. Leia quantity, unit_price, total vendido, nomes dos itens e cite os valores que aparecem. NÃO invente zeros nem informações. A resposta final = formatação dos dados retornados pela API.'
        };
        
        // Fazer nova chamada com os resultados das ferramentas
        const followUpMessages = [toolResponseInstruction, ...apiMessages];
        
        // Preparar payload da segunda chamada
        // IMPORTANTE: Não usar tool_choice: 'required' na segunda chamada para permitir resposta de texto
        const followUpPayload: any = {
          messages: followUpMessages,
          model: modelId,
          tools: requestBody.tools,
          tool_choice: 'auto' // Usar 'auto' para permitir que a IA responda com texto
        };
        
        // Adicionar temperature apenas para modelos que não são GPT-5
        const isGPT5Follow = modelId.includes('gpt-5') || modelId.includes('gpt-5.1');
        if (!isGPT5Follow) {
          followUpPayload.temperature = 0.7;
        }
        
        // Adicionar parâmetro de tokens correto
        if (requestBody.max_completion_tokens) {
          followUpPayload.max_completion_tokens = requestBody.max_completion_tokens;
        } else {
          followUpPayload.max_tokens = requestBody.max_tokens || 2000;
        }
        
        const followUpResponse = await this.fetchWithTimeout(
          `${supabaseUrl}/functions/v1/openai-proxy`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify(followUpPayload)
          }
        );

        if (!followUpResponse.ok) {
          throw new Error(`Erro na segunda chamada: ${followUpResponse.statusText}`);
        }

        data = await followUpResponse.json();
        message = data.choices?.[0]?.message;
        
        // ✨ LOOP RECURSIVO: Se GPT chamou mais tools (ex: generate_file), processar também
        if (message?.tool_calls && message.tool_calls.length > 0) {
          const { responses: moreToolResponses, files: moreFiles, executedTools: moreExecutedTools } = await this.processToolCalls(message.tool_calls);
          generatedFiles.push(...moreFiles);
          allExecutedTools.push(...moreExecutedTools);
          
          apiMessages.push(message);
          apiMessages.push(...moreToolResponses);
          
          // Terceira chamada (agora GPT deve responder com texto)
          const finalInstruction = {
            role: 'system' as const,
            content: 'REGRA OBRIGATÓRIA: Baseie sua resposta EXATAMENTE nos dados das mensagens role "tool" (resposta da API). Cite os valores que aparecem. NÃO invente zeros. Resposta final = formatação dos dados da API.'
          };
          
          // Preparar payload da terceira chamada
          const finalPayload: any = {
            messages: [finalInstruction, ...apiMessages],
            model: modelId
            // Não incluir tools aqui - queremos apenas texto final
          };
          
          // Adicionar temperature apenas para modelos que não são GPT-5
          const isGPT5Final = modelId.includes('gpt-5') || modelId.includes('gpt-5.1');
          if (!isGPT5Final) {
            finalPayload.temperature = 0.7;
          }
          
          // Adicionar parâmetro de tokens correto
          if (requestBody.max_completion_tokens) {
            finalPayload.max_completion_tokens = requestBody.max_completion_tokens;
          } else {
            finalPayload.max_tokens = requestBody.max_tokens || 2000;
          }
          
          const finalResponse = await this.fetchWithTimeout(
            `${supabaseUrl}/functions/v1/openai-proxy`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseAnonKey}`
              },
              body: JSON.stringify(finalPayload)
            }
          );
          
          if (!finalResponse.ok) {
            throw new Error(`Erro na terceira chamada: ${finalResponse.statusText}`);
          }
          
          data = await finalResponse.json();
          message = data.choices?.[0]?.message;
        }
      }
      
      // Se não tiver conteúdo mas tiver ferramentas executadas, forçar uma chamada final para gerar texto
      if (!message?.content && allExecutedTools.length > 0) {
        console.log('🔄 Forçando chamada final para gerar resposta de texto...');
        
        // Adicionar instrução para responder com texto baseado nos dados da API
        apiMessages.push({
          role: 'system',
          content: 'Agora você DEVE fornecer uma resposta em texto para o usuário. REGRA CRÍTICA: Baseie sua resposta EXATAMENTE nos dados das mensagens role "tool" acima (resposta da API). Leia e cite os valores (quantity, unit_price, total vendido, etc.) que aparecem. NÃO invente zeros nem informações. A resposta = formatação dos dados retornados pela API.'
        });
        
        const forceFinalPayload: any = {
          messages: apiMessages,
          model: modelId
          // Sem tools - forçar resposta de texto
        };
        
        const isGPT5ForceFinal = modelId.includes('gpt-5') || modelId.includes('gpt-5.1');
        if (!isGPT5ForceFinal) {
          forceFinalPayload.temperature = 0.7;
        }
        
        if (requestBody.max_completion_tokens) {
          forceFinalPayload.max_completion_tokens = requestBody.max_completion_tokens;
        } else {
          forceFinalPayload.max_tokens = requestBody.max_tokens || 2000;
        }
        
        const forceFinalResponse = await this.fetchWithTimeout(
          `${supabaseUrl}/functions/v1/openai-proxy`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify(forceFinalPayload)
          }
        );
        
        if (forceFinalResponse.ok) {
          const forceFinalData = await forceFinalResponse.json();
          message = forceFinalData.choices?.[0]?.message;
          data = forceFinalData;
        }
      }
      
      if (!message?.content) {
        console.error('❌ Mensagem sem content:', message);
        // Se temos ferramentas executadas mas sem conteúdo, criar uma resposta genérica
        if (allExecutedTools.length > 0) {
          const toolNames = allExecutedTools.map(t => t.name || t.toolId).join(', ');
          return {
            content: `Executei as ferramentas solicitadas (${toolNames}). Os resultados foram processados.`,
            tokens: data.usage,
            files: generatedFiles.length > 0 ? generatedFiles : undefined,
            toolCalls: allExecutedTools
          };
        }
        throw new Error('Resposta vazia da API OpenAI');
      }
      
      // Retornar conteúdo, tokens, arquivos gerados e tools executadas
      const result = {
        content: message.content,
        tokens: data.usage,
        files: generatedFiles.length > 0 ? generatedFiles : undefined,
        toolCalls: allExecutedTools.length > 0 ? allExecutedTools : undefined
      };
      
      console.log('✅ Resposta final:', {
        hasContent: !!result.content,
        contentLength: result.content?.length || 0,
        hasFiles: !!result.files,
        hasToolCalls: !!result.toolCalls,
        toolCallsCount: result.toolCalls?.length || 0
      });
      
      return result;
    } catch (error) {
      console.error('❌ Erro na API do OpenAI:', error);
      throw error;
    }
  }

  /**
   * Chamar API do Anthropic (Claude) via Supabase Edge Function
   */
  async callClaude(messages: Message[], modelId: string, contentRestrictions?: any): Promise<string> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL não configurado');
    }

    try {
      // ✅ Separar mensagens 'system' das outras mensagens
      const systemMessages = messages.filter(msg => msg.role === 'system');
      const conversationMessages = messages.filter(msg => msg.role !== 'system');
      
      // Preparar mensagens para Claude (com suporte a imagens)
      const apiMessages = await Promise.all(conversationMessages.map(async msg => {
        // Se a mensagem tem anexos de imagem
        if (msg.attachments && msg.attachments.length > 0) {
          const imageAttachments = msg.attachments.filter(att => att.type.startsWith('image/'));
          
          if (imageAttachments.length > 0) {
            // Formato para Claude (usa content array)
            const content: any[] = [];

            // Adicionar cada imagem
            for (const attachment of imageAttachments) {
              if (attachment.file) {
                
                // Claude tem limite de 5MB, comprimir se necessário
                const fileSizeMB = attachment.file.size / (1024 * 1024);
                let fileToSend = attachment.file;
                let mediaType = attachment.type;
                
                if (fileSizeMB > 4.5) { // Comprimir se maior que 4.5MB para margem de segurança
                  fileToSend = await this.compressImage(attachment.file, 4.5);
                  mediaType = 'image/jpeg'; // Após compressão, sempre é JPEG
                }
                
                const base64 = await this.fileToBase64(fileToSend);
                
                // Claude espera formato específico
                content.push({
                  type: 'image',
                  source: {
                    type: 'base64',
                    media_type: mediaType, // Usar o tipo correto (original ou jpeg após compressão)
                    data: base64
                  }
                });
              }
            }

            // Adicionar texto (se houver)
            if (msg.content) {
              content.push({
                type: 'text',
                text: msg.content
              });
            }

            return {
              role: msg.role === 'assistant' ? 'assistant' : 'user',
              content
            };
          }
        }

        // Mensagem normal sem imagens
        return {
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        };
      }));

      // ✅ Construir system prompt COMPLETO (instruções do assistente + restrições)
      let systemPrompt = '';
      
      // Adicionar instruções do assistente (mensagens 'system')
      if (systemMessages.length > 0) {
        systemPrompt = systemMessages.map(msg => msg.content).join('\n\n');
      }
      
      // Adicionar restrições de conteúdo se houver
      if (contentRestrictions && contentRestrictions.enabled) {
        const restrictionsPrompt = this.buildSystemPrompt(contentRestrictions);
        if (restrictionsPrompt) {
          systemPrompt = systemPrompt ? `${systemPrompt}\n\n${restrictionsPrompt}` : restrictionsPrompt;
        }
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const requestBody: any = {
        messages: apiMessages,
        model: modelId,
        temperature: 0.7,
        max_tokens: 2000
      };
      
      // ✅ Claude usa 'system' como parâmetro separado (não no array de mensagens)
      if (systemPrompt) {
        requestBody.system = systemPrompt;
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/anthropic-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        
        // Tratar erros específicos
        if (response.status === 401) {
          throw new Error('Chave de API inválida. Verifique sua chave da Anthropic.');
        } else if (response.status === 429) {
          throw new Error('Limite de uso excedido. Tente novamente em alguns minutos.');
        } else if (response.status === 400) {
          throw new Error('Requisição inválida. Verifique os parâmetros enviados.');
        } else {
          throw new Error(`Erro da API Anthropic: ${response.status} - ${errorText}`);
        }
      }

      const data = await response.json();
      
      const content = data.content?.[0]?.text;
      if (!content) {
        throw new Error('Resposta vazia da API Anthropic');
      }
      
      return content;
    } catch (error) {
      console.error('❌ Erro na API do Anthropic:', error);
      throw error;
    }
  }

  /**
   * Chamar API do Google (Gemini) via Supabase Edge Function
   */
  async callGemini(messages: Message[], modelId: string): Promise<string> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL não configurado');
    }

    try {
      // ✅ Separar mensagens 'system' das outras mensagens
      const systemMessages = messages.filter(msg => msg.role === 'system');
      const conversationMessages = messages.filter(msg => msg.role !== 'system');
      
      const apiMessages = conversationMessages.map(msg => ({
        role: msg.role === 'assistant' ? 'model' : 'user', // Gemini usa 'model' ao invés de 'assistant'
        content: msg.content
      }));

      // ✅ Gemini aceita system instruction como primeiro parâmetro
      let systemInstruction = '';
      if (systemMessages.length > 0) {
        systemInstruction = systemMessages.map(msg => msg.content).join('\n\n');
      }

      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const requestBody: any = {
        messages: apiMessages,
        model: modelId,
        temperature: 0.7,
        max_tokens: 2000
      };
      
      // Adicionar system instruction se houver
      if (systemInstruction) {
        requestBody.systemInstruction = systemInstruction;
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/google-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro da API Gemini: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || 'Sem resposta da API Gemini';
    } catch (error) {
      console.error('Erro na API Gemini:', error);
      throw error;
    }
  }


  /**
   * Método principal para enviar mensagem
   * Retorna conteúdo e tokens (quando disponível)
   */
  async sendMessage(
    provider: string, 
    messages: Message[], 
    modelId: string, 
    contentRestrictions?: any, 
    options?: { tenantId?: string; userId?: string; enabledTools?: string[]; forcedTools?: string[] }
  ): Promise<{ content: string; tokens?: any; files?: any[]; toolCalls?: any[] }> {
    // Enviar mensagem para provider
    
    // Verificar limite de uso diário
    if (options?.tenantId && options?.userId) {
      try {
        const result = await processMessageSent(options.tenantId, options.userId, provider);
        if (!result.success) {
          throw new Error(result.reason || 'Limite de mensagens excedido');
        }
      } catch (error) {
        throw new Error(`Limite de mensagens excedido: ${(error as Error).message}`);
      }
    }

    // Definir modelo padrão se não especificado
    if (!modelId) {
      switch (provider) {
        case 'openai':
          modelId = 'gpt-4o';
          break;
        case 'anthropic':
          modelId = 'claude-sonnet-4-6';
          break;
        case 'google':
          modelId = 'gemini-3-flash-preview';
          break;
        default:
          throw new Error(`Provedor não suportado: ${provider}`);
      }
    }

    // Chamar o provedor específico
    // 🔍 GPT decidirá automaticamente quando usar busca na web (via tools)
    try {
      let result: { content: string; tokens?: any; files?: any[]; toolCalls?: any[] };
      
      // Salvar userId para uso nas tools (ex: Google Calendar)
      this.currentUserId = options?.userId || null;
      
      switch (provider) {
        case 'openai':
          // GPT retorna content, tokens, files e toolCalls
          result = await this.callGPT(messages, modelId, contentRestrictions, options?.enabledTools, options?.forcedTools);
          break;
        case 'anthropic':
          // Claude retorna string, vamos adaptar
          const claudeResponse = await this.callClaude(messages, modelId, contentRestrictions);
          result = { content: claudeResponse };
          break;
        case 'google':
          // Gemini retorna string, vamos adaptar
          const geminiResponse = await this.callGemini(messages, modelId);
          result = { content: geminiResponse };
          break;
        default:
          throw new Error(`Provedor não suportado: ${provider}`);
      }
      
      // Garantir que files e toolCalls sejam retornados
      console.log('🔄 sendMessage retornando:', {
        hasContent: !!result.content,
        hasFiles: !!result.files,
        filesCount: result.files?.length || 0,
        hasToolCalls: !!result.toolCalls,
        toolCallsCount: result.toolCalls?.length || 0
      });
      
      return result;
    } catch (error) {
      console.error(`Erro no provedor ${provider}:`, error);
      throw error;
    }
  }
}

export const aiService = AIService.getInstance();